"use client";
import { usePathname } from "next/navigation";
import { useEffect, useState, useMemo } from "react";
import { Inter } from "next/font/google";
import { Monitor } from "lucide-react";
import { useAuthGuard } from "@/lib/hooks/useAuthGuard";
import { clearAuthForTesting, isTestingMode } from "@/lib/config";
import { ToastProvider } from "@/lib/contexts/ToastContext";
import ToastContainer from "@/components/ToastContainer";
import "./globals.css";

/**
 * Inter, exposed as a CSS variable rather than a class so `--font-sans` in
 * globals.css resolves to it and every surface inherits without being told.
 *
 * The console was on Gilmer, which has no weights beyond regular here and is
 * loaded as a single unhinted OTF — so every "semibold" heading in the app was
 * being synthesised by the browser. The V4 type ramp is tuned for Inter: the
 * negative tracking that holds a 34px heading together is Inter's requirement,
 * and applied to Gilmer it sets the heading too tight. The font and the ramp
 * are one decision and they move together.
 */
const inter = Inter({
  subsets: ["latin"],
  display: "swap",
  variable: "--font-inter",
});

/**
 * The shell around every full-screen state below — the pre-hydration splash,
 * the auth check, and the real app.
 *
 * Hoisted so the three cannot drift: before this they were three separate
 * `<html>` trees and only one of them carried the font variable, so the splash
 * rendered in the system stack and the app snapped to Inter a frame later.
 */
function Shell({ children }) {
  return (
    <html lang="en" className={inter.variable}>
      <head>
        <title>InversePay Dashboard</title>
        <meta name="description" content="Borderless Finance" />
        {/* The browser chrome follows the page. Without this the address bar
            on a tablet stays its default grey behind a white console. */}
        <meta name="theme-color" content="#ffffff" />
      </head>
      <body>{children}</body>
    </html>
  );
}

/**
 * The one thing shown while the console works out whether you are signed in.
 *
 * A bare spinner on a grey page is indistinguishable from a hung request, so
 * this says what it is waiting for. The mark is the brand gradient rather than
 * a logo file — it needs no network round-trip, which matters for a screen
 * whose entire job is to appear instantly.
 */
function Splash({ message }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-canvas">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-dot-grid mask-fade-b opacity-50"
      />
      <span className="relative flex size-11 items-center justify-center rounded-xl bg-brand-gradient shadow-raised">
        <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      </span>
      <p className="relative text-sm text-muted">{message}</p>
    </div>
  );
}

export default function RootLayout({ children }) {
  const pathname = usePathname();
  const [isClient, setIsClient] = useState(false);

  // Ensure client-side rendering
  useEffect(() => {
    setIsClient(true);
    
    // Load debug utilities only in non-production and only on client side
    if (process.env.NEXT_PUBLIC_ENV === 'development' && typeof window !== 'undefined') {
      import("@/lib/utils/debugAuth").catch(err => {
        console.error('Failed to load debug utilities:', err);
      });
    }
  }, []);

  // Clear authentication in testing mode on app start
  useEffect(() => {
    if (isTestingMode()) {
      clearAuthForTesting();
    }
  }, []);

  // Stable route classification using useMemo
  const routeConfig = useMemo(() => {
    const authRoutes = ['/login', '/verifyAccount', '/createPassword', '/changePassword', '/OTP', '/resetPassword'];
    const publicRoutes = ['/support/track']; // Guest ticket tracking
    const isAuthRoute = authRoutes.some(route => pathname.startsWith(route));
    const isPublicRoute = publicRoutes.some(route => pathname.startsWith(route));
    const isRootRoute = pathname === '/';

    return {
      authRoutes,
      publicRoutes,
      isAuthRoute,
      isPublicRoute,
      isRootRoute,
      requireAuth: !isAuthRoute && !isPublicRoute && !isRootRoute,
      redirectIfAuthenticated: isAuthRoute ? '/dashboard' : null
    };
  }, [pathname]);

  // Debug route classification and authentication state - only in development
  useEffect(() => {
    if (isClient && process.env.NEXT_PUBLIC_ENV === 'development') {
      console.log('🔍 Route Classification Debug:', {
        pathname,
        ...routeConfig
      });

      // Log authentication state for security debugging
      if (typeof window !== 'undefined') {
        import('@/lib/services/auth').then(({ authService }) => {
          const authState = authService.getAuthState();
          console.log('🔐 Security State Debug:', authState);
        });
      }
    }
  }, [pathname, routeConfig, isClient]);

  // Use auth guard based on route type (only on client side)
  const { isLoading } = useAuthGuard({
    requireAuth: routeConfig.requireAuth,
    redirectTo: '/login',
    redirectIfAuthenticated: routeConfig.redirectIfAuthenticated
  });

  // Session keepalive - proactively refresh tokens to maintain persistent login
  useEffect(() => {
    if (!isClient || routeConfig.isAuthRoute || routeConfig.isPublicRoute || isTestingMode()) {
      return; // Don't run on auth pages, public pages, or in testing mode
    }

    let refreshInterval;

    const setupSessionKeepalive = async () => {
      try {
        const { authService } = await import('@/lib/services/auth');
        
        // Check if user is authenticated
        if (!authService.isAuthenticated()) {
          return;
        }

        // Set up periodic token refresh check (every 4 minutes)
        // This ensures tokens are refreshed before they expire (typically 15 min expiry)
        refreshInterval = setInterval(async () => {
          try {
            const expiresAt = localStorage.getItem('token_expires_at');
            if (!expiresAt) {
              console.log('🔄 No token expiration found, attempting refresh');
              await authService.refreshAccessToken();
              return;
            }

            const expiryTime = new Date(expiresAt).getTime();
            const now = Date.now();
            const timeUntilExpiry = expiryTime - now;
            const fiveMinutes = 5 * 60 * 1000;

            // Refresh if token expires in less than 5 minutes
            if (timeUntilExpiry < fiveMinutes) {
              console.log(`🔄 Token expires in ${Math.round(timeUntilExpiry / 1000)}s, refreshing proactively`);
              const refreshed = await authService.refreshAccessToken();
              if (refreshed) {
                console.log('✅ Session keepalive: Token refreshed successfully');
              } else {
                console.log('❌ Session keepalive: Token refresh failed');
              }
            }
          } catch (error) {
            console.error('Session keepalive error:', error);
          }
        }, 4 * 60 * 1000); // Check every 4 minutes

        console.log('✅ Session keepalive initialized - tokens will refresh automatically');
      } catch (error) {
        console.error('Failed to setup session keepalive:', error);
      }
    };

    setupSessionKeepalive();

    // Cleanup on unmount
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        console.log('🛑 Session keepalive stopped');
      }
    };
  }, [isClient, routeConfig.isAuthRoute]);

  // Add dashboard debugging utilities to browser console - only in development
  useEffect(() => {
    if (isClient && typeof window !== 'undefined' && process.env.NEXT_PUBLIC_ENV === 'development') {
      // Create debugging utilities directly in browser
      const clearAllCachedData = () => {
        console.log('🧹 Clearing all cached data...');

        // Clear localStorage
        console.log('🧹 Clearing localStorage...');
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          console.log(`  🗑️ Removing: ${key}`);
          localStorage.removeItem(key);
        });

        // Clear sessionStorage
        console.log('🧹 Clearing sessionStorage...');
        sessionStorage.clear();

        console.log('✅ All cached data cleared!');
      };

      const forceReloadDashboard = () => {
        console.log('🔄 Force reloading dashboard...');
        clearAllCachedData();
        setTimeout(() => {
          console.log('🔄 Forcing hard refresh...');
          window.location.reload(true);
        }, 1000);
      };

      const traceDashboardDataFlow = async () => {
        console.log('🔍 TRACING DASHBOARD DATA FLOW...');
        console.log('================================');

        // Check environment
        console.log('1. Environment Check:');
        console.log(`  📊 NODE_ENV: ${process.env.NODE_ENV}`);
        console.log(`  🧪 TESTING_DATA: ${process.env.NEXT_PUBLIC_TESTING_DATA}`);
        console.log(`  ✅ Testing Mode: ${isTestingMode()}`);

        // Check API configuration
        console.log('\n2. API Configuration:');
        const { getApiBaseUrl } = await import('@/lib/config');
        const apiBaseUrl = getApiBaseUrl();
        console.log(`  🌐 API Base URL: ${apiBaseUrl}`);

        // Check for cached data
        console.log('\n3. Cached Data Check:');
        const localStorageKeys = Object.keys(localStorage);
        const sessionStorageKeys = Object.keys(sessionStorage);

        console.log(`  📦 localStorage keys: ${localStorageKeys.length > 0 ? localStorageKeys.join(', ') : 'none'}`);
        console.log(`  📦 sessionStorage keys: ${sessionStorageKeys.length > 0 ? sessionStorageKeys.join(', ') : 'none'}`);

        // Check for specific dashboard keys
        const dashboardKeys = localStorageKeys.filter(key => key.includes('dashboard'));
        if (dashboardKeys.length > 0) {
          console.log(`  ⚠️  Found dashboard cache keys: ${dashboardKeys.join(', ')}`);
          dashboardKeys.forEach(key => {
            console.log(`    🔍 ${key}:`, localStorage.getItem(key));
          });
        }

        // Make a test API call to trace the exact response
        console.log('\n4. Testing API Call:');
        try {
          console.log(`  📞 Making test API call to ${apiBaseUrl}/api/v1/admin/dashboard/overview...`);

          const response = await fetch(`${apiBaseUrl}/api/v1/admin/dashboard/overview?period=monthly`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('admin_access_token')}`,
              'Content-Type': 'application/json'
            }
          });

          console.log(`  📨 Response Status: ${response.status}`);
          const data = await response.json();
          console.log('  📊 Raw API Response:', JSON.stringify(data, null, 2));

          console.log('\n  🔍 SPECIFIC VALUES FROM API:');
          console.log(`    💰 Revenue: ${data.total_revenue?.current} ${data.total_revenue?.currency}`);
          console.log(`    📈 Volume: ${data.transaction_volume?.current}`);
          console.log(`    👥 Users: ${data.active_users?.current}`);

        } catch (error) {
          console.error('  ❌ API call failed:', error);
        }

        console.log('\n================================');
        console.log('🔍 Data flow trace complete. Check the logs above to identify where dummy data is coming from.');
      };

      const compareDisplayedVsApiValues = (displayedValues = {}) => {
        console.log('🔍 COMPARING DISPLAYED VS API VALUES...');
        console.log('=====================================');

        console.log('📊 Currently Displayed Values:');
        console.log(`  💰 Revenue: ${displayedValues.revenue || 'unknown'}`);
        console.log(`  📈 Volume: ${displayedValues.volume || 'unknown'}`);
        console.log(`  👥 Users: ${displayedValues.users || 'unknown'}`);

        console.log('\n📊 Expected API Values (from logs):');
        console.log('  💰 Revenue: 0 RWF');
        console.log('  📈 Volume: 0');
        console.log('  👥 Users: 0');

        console.log('\n🔍 Analysis:');
        if (displayedValues.revenue && displayedValues.revenue !== '0' && displayedValues.revenue !== '0 RWF') {
          console.log('  ⚠️  ISSUE FOUND: Revenue is not showing API value (0)');
        }
        if (displayedValues.volume && displayedValues.volume !== '0') {
          console.log('  ⚠️  ISSUE FOUND: Volume is not showing API value (0)');
        }
        if (displayedValues.users && displayedValues.users !== '0') {
          console.log('  ⚠️  ISSUE FOUND: Users is not showing API value (0)');
        }

        console.log('\n💡 RECOMMENDED ACTIONS:');
        console.log('  1. Run clearAllCachedData() to clear any cached values');
        console.log('  2. Run forceReloadDashboard() to force a complete refresh');
        console.log('  3. Check browser Network tab to verify API calls are returning expected values');
        console.log('  4. Check React DevTools to see the actual props being passed to InfoCard components');
      };

      // Make debug functions available globally
      window.debugDashboard = {
        clearAllCachedData,
        forceReloadDashboard,
        traceDashboardDataFlow,
        compareDisplayedVsApiValues,

        // Quick fix function
        quickFix: () => {
          console.log('🚀 QUICK FIX: Clearing cache and reloading...');
          clearAllCachedData();
          setTimeout(() => {
            forceReloadDashboard();
          }, 1000);
        }
      };

      console.log('🛠️  Dashboard Debug Utilities Loaded! Available commands:');
      console.log('  debugDashboard.quickFix() - Clear cache and reload');
      console.log('  debugDashboard.traceDashboardDataFlow() - Trace data flow');
      console.log('  debugDashboard.compareDisplayedVsApiValues({revenue: "21,219,890", volume: "124,093", users: "398,904"}) - Compare values');
      console.log('  debugDashboard.clearAllCachedData() - Clear all cached data');
    }
  }, [isClient]);

  // Don't render until client-side
  if (!isClient) {
    return (
      <Shell>
        <Splash message="Starting the console…" />
      </Shell>
    );
  }

  // Show loading for authentication check (skip in testing mode and auth routes)
  if (isLoading && !routeConfig.isRootRoute && !routeConfig.isAuthRoute && !isTestingMode()) {
    return (
      <Shell>
        <Splash message="Checking your session…" />
      </Shell>
    );
  }

  return (
    <Shell>
      <ToastProvider>
        <ToastContainer />
        <div className="hidden lg:flex">{children}</div>

        {/* Below 1024px the console is not narrowed, it is withheld. Settled as
            a product decision in IN-311/IN-313 — see docs/decisions.md for the
            reasoning and for the read-only triage surface that was considered
            and deferred. These screens are dense tables of financial and
            compliance data, and a hidden column on a KYC review is worse than
            no screen at all. The copy states that rather than apologising for
            a page that failed to load. */}
        <div className="flex min-h-screen items-center justify-center px-6 lg:hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-dot-grid mask-fade-b opacity-50"
          />
          <div className="relative flex max-w-sm flex-col items-center gap-3 text-center">
            <span className="flex size-11 items-center justify-center rounded-xl border border-line bg-surface text-muted-soft shadow-xs">
              <Monitor className="size-5" aria-hidden="true" />
            </span>
            <p className="text-[15px] font-semibold text-ink">
              InversePay Admin is a desktop console
            </p>
            <p className="text-sm leading-5 text-muted">
              It shows dense tables of transaction and compliance data, and
              those are not safe to fold into a phone — a hidden column on a
              KYC review is worse than no screen at all. Open it on a display
              at least 1024px wide.
            </p>
          </div>
        </div>
      </ToastProvider>
    </Shell>
  );
}
